// @file Vuex Store for Dashboard settings basic info in Dashboard settings
import { captureException, captureFetchException } from '@@/bits/error_tracker'
import * as PadletApi from '@@/dashboard/padlet_api'
import type { UserId } from '@@/types'
import type { DashboardSettingsState } from '@@/vuexstore/dashboard_settings'
import type { Module } from 'vuex'

type SubmissionState = 'success' | 'validationError' | 'serverError' | ''
interface SubmissionError {
  avatar: string
  name: string
  email: string
  username: string
  bio: string
}

interface BasicInfoState {
  id: UserId | null
  avatar: string
  name: string
  email: string
  username: string
  bio: string
  lang: string
  is_teacher: boolean
  submissionState: SubmissionState
  error: SubmissionError
}

const initialState = (): BasicInfoState => {
  return {
    id: null,
    avatar: '',
    name: '',
    email: '',
    username: '',
    bio: '',
    lang: '',
    is_teacher: false,
    submissionState: '',
    error: {
      avatar: '',
      name: '',
      email: '',
      username: '',
      bio: '',
    },
  }
}

const basicInfo: Module<BasicInfoState, DashboardSettingsState> = {
  namespaced: true,
  state: initialState(),
  getters: {
    nameErrorMessage: (state): string => state.error.name,
    isNameValid: (_state, getters): boolean => {
      return !!getters.nameErrorMessage.length
    },
  },
  mutations: {
    INITIALIZE_STATE(state, data): void {
      Object.keys(state).forEach((key) => {
        state[key] = data[key] ? data[key] : state[key]
      })
    },
    RESET_USER_BASIC_INFO(state, originalData): void {
      Object.keys(state).forEach((key) => {
        state[key] = originalData[key] !== undefined ? originalData[key] : state[key]
      })
    },
    RESET_BASIC_INFO_ERROR_AND_SUBMISSION_STATE(state): void {
      state.submissionState = initialState().submissionState
      state.error = { ...initialState().error }
    },
    UPDATE_USER_AVATAR(state, url): void {
      state.avatar = url
    },
    UPDATE_USER_FULL_NAME(state, name): void {
      state.name = name
    },
    UPDATE_USER_EMAIL(state, email): void {
      state.email = email
    },
    UPDATE_USER_USERNAME(state, username): void {
      state.username = username
    },
    UPDATE_USER_BIO(state, bio): void {
      state.bio = bio
    },
    UPDATE_USER_LANGUAGE(state, lang): void {
      state.lang = lang
    },
    UPDATE_USER_IS_TEACHER(state, isTeacher): void {
      state.is_teacher = isTeacher
    },
    UPDATE_BASIC_INFO_ERROR(state, { field, message }): void {
      state.error = { ...state.error, [field]: message }
    },
    UPDATE_BASIC_INFO_SUBMISSION_STATE(state, status): void {
      state.submissionState = status
    },
  },
  actions: {
    initializeState({ commit }, data): void {
      commit('INITIALIZE_STATE', data)
    },
    resetUserBasicInfo({ commit }, originalData): void {
      commit('RESET_USER_BASIC_INFO', originalData)
    },
    resetBasicInfoErrorAndSubmissionState({ commit }): void {
      commit('RESET_BASIC_INFO_ERROR_AND_SUBMISSION_STATE')
    },
    updateUserAvatar({ commit }, url): void {
      commit('UPDATE_USER_AVATAR', url)
    },
    updateUserFullName({ commit }, name): void {
      commit('UPDATE_USER_FULL_NAME', name)
    },
    updateUserEmail({ commit }, email): void {
      commit('UPDATE_USER_EMAIL', email)
    },
    updateUserUsername({ commit }, username): void {
      commit('UPDATE_USER_USERNAME', username)
    },
    updateUserBio({ commit }, bio): void {
      commit('UPDATE_USER_BIO', bio)
    },
    updateUserLanguage({ commit }, language): void {
      commit('UPDATE_USER_LANGUAGE', language)
    },
    updateUserIsTeacher({ commit }, isTeacher): void {
      commit('UPDATE_USER_IS_TEACHER', isTeacher)
    },
    updateBasicInfoError({ commit }, error): void {
      commit('UPDATE_BASIC_INFO_ERROR', error)
    },
    updateBasicInfoSubmissionState({ commit }, status): void {
      commit('UPDATE_BASIC_INFO_SUBMISSION_STATE', status)
    },
    async saveUserInfo({ commit }, { userId, payload }): Promise<void> {
      try {
        const body = JSON.stringify(payload)
        const data = await PadletApi.User.updateBasicInfo(userId, { body })
        commit('INITIALIZE_STATE', data)
      } catch (e) {
        const UNPROCESSABLE_ENTITY = 422
        if (e.status === UNPROCESSABLE_ENTITY) {
          try {
            const parsedErrorMessage = JSON.parse(e.message)

            for (const field in parsedErrorMessage) {
              commit('UPDATE_BASIC_INFO_ERROR', { field, message: parsedErrorMessage[field] })
            }
          } catch (e) {
            captureException(e)
          }
        } else {
          captureFetchException(e, { source: 'BasicInfoSaveUserInfo' })
        }
        throw e
      }
    },
  },
}

export default basicInfo
